import { describeResource } from '@siteless/resource';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { database } from '../../config/firebase';
import z, { core } from 'zod';
import ballotDisplayOrder from '../Ballot/constants/ballotDisplayOrder';
import events from '../Ballot/constants/events';
import { oscarDatabase, thisOscarsYear } from '../../config/constants';
import { isEmpty } from 'lodash';

let firestoreTimestampSchema = z.object({
  nanoseconds: z.number(),
  seconds: z.number(),
});

// TODO: centralize these with BE
let ceremonyDetailsSchema = z.object({
  ceremonyStartAt: firestoreTimestampSchema.optional(),
  ceremonyStartAtSource: z.string().optional(),
  ceremonyHost: z.string().optional(),
  ceremonyHostSource: z.string().optional(),
  nominationsAnnouncementAt: firestoreTimestampSchema.optional(),
  nominationsAnnouncementAtSource: z.string().optional(),
  shortlistsRevealAt: firestoreTimestampSchema.optional(),
  shortlistsRevealAtSourceOptional: z.string().optional(),
  votingEndsAt: firestoreTimestampSchema.optional(),
  votingEndsAtSourceOptional: z.string().optional(),
});

export type CeremonyDetails = z.infer<typeof ceremonyDetailsSchema>;

let coreObjectiveStateSchema = z.object({
  results: z.array(
    z.record(
      z.string(),
      z.union([z.null(), z.string()]),
    ),
  ),
  ceremonyStartAt: firestoreTimestampSchema,
  ceremonyDetails: ceremonyDetailsSchema,
  isBallotReady: z.boolean().optional(),
  didCeremonyEnd: z.boolean(),
  didCeremonyStart: z.boolean(),
  forceReloadToken: z.number(),
  events: z.object({
    thanksAcademy: z.number().optional(),
    thanksGod: z.number().optional(),
    thanksMom: z.number().optional(),
    wrongWinner: z.boolean().optional(),
  }),
});

function normalizeCoreObjectiveState(raw: Record<string, unknown>) {
  const rawCeremonyDetails = raw.ceremonyDetails as Record<string, unknown>;
  const ceremonyStartAt = rawCeremonyDetails?.ceremonyStartAt;

  return {
    ...raw,
    ceremonyStartAt,
    events: {
      thanksAcademy: (raw.events as Record<string, unknown>)?.thanksAcademy ?? 0,
      thanksGod: (raw.events as Record<string, unknown>)?.thanksGod ?? 0,
      thanksMom: (raw.events as Record<string, unknown>)?.thanksMom ?? 0,
      wrongWinner: (raw.events as Record<string, unknown>)?.wrongWinner ?? false,
    },
  };
}

export let ballotShapeSchema = z.record(
  z.string(),
  z.object({
    displayName: z.string(),
    options: z.record(
      z.string(),
      z.discriminatedUnion('type', [
        z.object({
          type: z.literal('eventCount'),
          data: z.object({
            eventName: z.string(),
            points: z.number(),
          }),
        }),
        z.object({
          type: z.literal('eventBoolean'),
          data: z.object({
            eventName: z.string(),
            points: z.number(),
          }),
        }),
        z.object({
          type: z.literal('winner'),
          data: z.object({
            categoryKey: z.string(),
            nomineeId: z.string(),
            points: z.number(),
          }),
        }),
        z.object({
          type: z.literal('pass'),
          data: z.object({
            categoryKey: z.string(),
            points: z.number(),
          }),
        }),
      ]),
    ),
  }),
);

export type ObjectiveStateResourceType = z.infer<
  typeof coreObjectiveStateSchema
> & {
  films: typeof oscarDatabase.films;
  people: typeof oscarDatabase.people;
  nomineeCategories: typeof oscarDatabase.nomineeCategories & { thankYou: any };
  ballotShape: Record<
    (typeof ballotDisplayOrder)[number],
    {
      displayName: string;
      options: Record<
        string,
        | {
          type: 'eventCount';
          data: {
            eventName: keyof typeof events;
            points: number;
          };
        }
        | {
          type: 'eventBoolean';
          data: {
            eventName: keyof typeof events;
            points: number;
          };
        }
        | {
          type: 'winner';
          data: {
            categoryKey: keyof typeof oscarDatabase.nomineeCategories;
            nomineeId: string;
            points: number;
          };
        }
        | {
          type: 'pass';
          data: {
            categoryKey: keyof typeof oscarDatabase.nomineeCategories;
            points: number;
          };
        }
      >;
    }
  >;
};

let ObjectiveStateResource = describeResource<
  undefined | number,
  ObjectiveStateResourceType
>('ObjectiveState', {
  get: async (oscarsYear = thisOscarsYear) => {
    let rawData = (await getDoc(doc(database, 'objective_state', String(oscarsYear)))).data();

    let coreData = coreObjectiveStateSchema.parse(
      normalizeCoreObjectiveState(rawData as Record<string, unknown>),
    );



    let ballotShape: any = window.__NEXT_DATA__.props.pageProps?.data?.ballotShape || {};

    if (isEmpty(ballotShape)) {

      console.warn('ObjectiveStateResource.get ballot shape cache miss');

      let detailsPromises: Promise<any>[] = [];

      (
        await getDocs(collection(database, 'objective_state', String(oscarsYear), 'ballotShape'))
      ).forEach(ballotShapeSection => {
        ballotShape = {
          ...ballotShape,
          [ballotShapeSection.id]: {
            ...ballotShapeSection.data(),
            options: {},
          },
        };
      });

      Object.entries(ballotShape).forEach(([sectionId, section]) => {
        detailsPromises.push(
          (async () => {
            (
              await getDocs(
                collection(
                  database,
                  'objective_state',
                  String(oscarsYear),
                  'ballotShape',
                  sectionId,
                  'options',
                )
              )
            ).forEach(option => {
              const optionData = option.data();

              ballotShape = {
                ...ballotShape,
                [sectionId]: {
                  ...ballotShape[sectionId],
                  options: {
                    ...ballotShape[sectionId].options,
                    [option.id]: optionData,
                  },
                },
              };
            });
          })(),
        );
      });

      await Promise.all(detailsPromises);
    }




    return {
      ...coreData,
      ballotShape: ballotShapeSchema.parse(
        ballotShape,
      ) as unknown as ObjectiveStateResourceType['ballotShape'],
      films: oscarDatabase.films,
      people: oscarDatabase.people,
      nomineeCategories:
        oscarDatabase.nomineeCategories as unknown as ObjectiveStateResourceType['nomineeCategories'],
    };
  },
});

export default ObjectiveStateResource;
